import React from 'react';
import { Instagram, Facebook, Mail } from 'lucide-react';
import { bakeryInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const ModernFooter = () => {
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';

  const getLabel = (key) => {
    const labels = {
      followUs: { ar: 'تابعنا', de: 'Folgen Sie uns', en: 'Follow Us', fr: 'Suivez-nous', tr: 'Bizi Takip Edin', uk: 'Підписуйтесь', ku: 'Me bişopînin' },
      paymentMethods: { ar: 'طرق الدفع المتاحة', de: 'Verfügbare Zahlungsmethoden', en: 'Payment Methods', fr: 'Méthodes de paiement', tr: 'Ödeme Yöntemleri', uk: 'Способи оплати', ku: 'Rêbazên dravdanê' }
    };
    return labels[key]?.[currentLang] || labels[key]?.en || key;
  };

  const navLinks = [
    { path: '/', label: t.nav?.home || 'الرئيسية' },
    { path: '/products', label: t.nav?.products || 'منتجاتنا' },
    { path: '/about', label: t.nav?.about || 'من نحن' },
    { path: '/contact', label: t.nav?.contact || 'تواصل معنا' }
  ];

  return (
    <footer className="bg-[#0d4023] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className={`lg:col-span-1 ${isRTL ? 'text-end' : 'text-start'}`}>
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img 
                src={bakeryInfo.logo} 
                alt="Dura Alsham"
                className="w-14 h-14 object-contain"
              />
              <div>
                <h3 className="font-heading text-xl font-bold text-white">
                  Dura Alsham
                </h3>
                <p className="text-sm text-white/50">{t.hero?.tagline || 'Authentic Levantine Bread'}</p>
              </div>
            </div>
            <p className="text-white/60 leading-relaxed text-sm">
              {t.hero?.description || 'نقدم أشهى أنواع الخبز العربي والمعجنات الشرقية بأيدي خبراء من بلاد الشام'}
            </p>
          </div>

          {/* Quick Links */}
          <div className={isRTL ? 'text-end' : 'text-start'}>
            <h4 className="font-heading text-lg font-bold text-[#b89759] mb-6">
              {t.footer?.quickLinks || 'روابط سريعة'}
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <a 
                    href={link.path}
                    className="text-white/60 hover:text-[#b89759] transition-colors duration-300 text-sm"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className={isRTL ? 'text-end' : 'text-start'}>
            <h4 className="font-heading text-lg font-bold text-[#b89759] mb-6">
              {t.footer?.contactUs || 'معلومات الاتصال'}
            </h4>
            <ul className="space-y-3 text-white/60 text-sm">
              <li>
                <a href={`tel:${bakeryInfo.phone}`} className="hover:text-[#b89759] transition-colors" dir="ltr">
                  {bakeryInfo.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${bakeryInfo.email}`} className="hover:text-[#b89759] transition-colors" dir="ltr">
                  {bakeryInfo.email}
                </a>
              </li>
              <li>{t.contact?.hoursValue || 'يومياً: 12:00 AM - 10:00 PM'}</li>
              <li className="pt-2">{bakeryInfo.address}</li>
            </ul>
          </div>

          {/* Follow Us */}
          <div className={isRTL ? 'text-end' : 'text-start'}>
            <h4 className="font-heading text-lg font-bold text-[#b89759] mb-6">
              {getLabel('followUs')}
            </h4>
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
              <a 
                href="https://www.tiktok.com/@dura.alsham"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#b89759] hover:border-[#b89759] transition-colors duration-300"
                data-testid="social-tiktok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#b89759] hover:border-[#b89759] transition-colors duration-300"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#b89759] hover:border-[#b89759] transition-colors duration-300"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={`mailto:${bakeryInfo.email}`}
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#b89759] hover:border-[#b89759] transition-colors duration-300"
                data-testid="social-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>

            {/* Payment Methods */}
            <div className="mt-8">
              <h5 className="text-xs uppercase tracking-wider text-white/40 mb-4">
                {getLabel('paymentMethods')}
              </h5>
              <div className={`flex gap-2 flex-wrap ${isRTL ? 'justify-end' : ''}`}>
                <div className="bg-white/5 px-3 py-1.5 rounded border border-white/10">
                  <span className="text-xs font-medium text-white/70">Visa</span>
                </div>
                <div className="bg-white/5 px-3 py-1.5 rounded border border-white/10">
                  <span className="text-xs font-medium text-white/70">MC</span>
                </div>
                <div className="bg-white/5 px-3 py-1.5 rounded border border-white/10">
                  <span className="text-xs font-medium text-white/70">PayPal</span>
                </div>
                <div className="bg-white/5 px-3 py-1.5 rounded border border-white/10">
                  <span className="text-xs font-medium text-white/70">Cash</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className={`flex flex-col md:flex-row justify-between items-center gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <p className="text-white/40 text-sm text-center">
              © {new Date().getFullYear()} Dura Alsham. {t.footer?.rights || 'جميع الحقوق محفوظة'}.
            </p>
            <p className="text-white/40 text-sm text-center">
              {t.footer?.madeWith || 'Made with'} <span className="text-red-400">♥</span> {t.footer?.inGermany || 'in Germany'}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;
